const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const DEBUG_STORAGE_KEY = "palletPicker.debug.enabled";
const DEBUG_EVENT_LIMIT = 50;
const debugEvents = [];

export function isApiDebugEnabled() {
  try {
    return JSON.parse(window.localStorage.getItem(DEBUG_STORAGE_KEY) || "false") === true;
  } catch {
    return false;
  }
}

export function setApiDebugEnabled(enabled) {
  try {
    window.localStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(Boolean(enabled)));
  } catch {
    // Ignore storage failures; diagnostics remain best-effort.
  }
}

export function getApiDebugEvents() {
  return [...debugEvents];
}

export function clearApiDebugEvents() {
  debugEvents.splice(0, debugEvents.length);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("palletPicker:api-debug-event"));
  }
}

function recordApiDebugEvent(event) {
  if (!isApiDebugEnabled()) {
    return;
  }

  const safeEvent = {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    at: new Date().toISOString(),
    ...event,
  };

  debugEvents.unshift(safeEvent);
  debugEvents.splice(DEBUG_EVENT_LIMIT);

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("palletPicker:api-debug-event", { detail: safeEvent })
    );
  }
}

async function request(path, options = {}) {
  const method = options.method || "GET";
  const startedAt =
    typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
  const headers = options.body instanceof FormData ? {} : { "Content-Type": "application/json" };
  let response;

  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    recordApiDebugEvent({
      method,
      path,
      status: "network-error",
      ok: false,
      durationMs: Math.round(
        (typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now()) - startedAt
      ),
      error: error.message,
    });
    throw error;
  }

  if (response.status === 204) {
    recordApiDebugEvent({
      method,
      path,
      status: response.status,
      ok: true,
      durationMs: Math.round(
        (typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now()) - startedAt
      ),
    });
    return null;
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.error?.message || "Request failed";
    recordApiDebugEvent({
      method,
      path,
      status: response.status,
      ok: false,
      durationMs: Math.round(
        (typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now()) - startedAt
      ),
      error: message,
    });
    throw new Error(message);
  }

  recordApiDebugEvent({
    method,
    path,
    status: response.status,
    ok: true,
    durationMs: Math.round(
      (typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now()) - startedAt
    ),
  });
  return payload;
}

export function getItems() {
  return request("/items");
}

export function createItem(itemName) {
  return request("/items", {
    method: "POST",
    body: JSON.stringify(itemName ? { item_name: itemName } : {}),
  });
}

export function updateItem(id, fields) {
  const body = typeof fields === "string" ? { item_name: fields } : fields;
  return request(`/items/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteItem(id) {
  return request(`/items/${id}`, {
    method: "DELETE",
  });
}

export function getHistoricItemNames() {
  return request("/item-names");
}

export function deleteHistoricItemName(id) {
  return request(`/item-names/${id}`, {
    method: "DELETE",
  });
}

export function getLocations() {
  return request("/locations");
}

export function createLocation(itemId, location) {
  return request(`/items/${itemId}/locations`, {
    method: "POST",
    body: JSON.stringify(location),
  });
}

export function updateLocation(id, fields) {
  return request(`/locations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
}

export function deleteLocation(id) {
  return request(`/locations/${id}`, {
    method: "DELETE",
  });
}

export function importLocationsCsv(file, dryRun) {
  const formData = new FormData();
  formData.append("file", file);
  return request(`/import/locations?dryRun=${dryRun ? "true" : "false"}`, {
    method: "POST",
    body: formData,
  });
}

export function getDebugStatus() {
  return request("/debug/status");
}

export function runDebugChecks({ checks = [], allowWrites = false } = {}) {
  return request("/debug/checks", {
    method: "POST",
    body: JSON.stringify({ checks, allowWrites }),
  });
}
